import {
  GetTripQuery,
  ShipmentLocationType,
  Trip,
} from "../../../../graphql/generated";
import { Box, Typography, useTheme } from "@mui/material";
import { groupBy, map } from "lodash";
import Map, {
  createMarker,
  createPolylineFromString,
  InfoWindowContent,
  MarkerData,
  PolylineData,
} from "../../../common/Map/Map";
import toLatLng from "../../../../utils/geo/toLaLng";
import { ReactNode, useMemo, useState } from "react";
import pickupPin from "../../../../assets/icons/pickup-pin.svg";
import dropoffPin from "../../../../assets/icons/dropoff-pin.svg";
import truckPin from "../../../../assets/icons/truck-pin.svg";
import driverLabel from "../../../../utils/labels/driverLabel";
import tractorLabel from "../../../../utils/labels/tractorLabel";
import trailerLabel from "../../../../utils/labels/trailerLabel";
import { formatDateTime } from "../../../../utils/labels/formatDateTime";
import {
  shipmentLocationLabel,
  shipmentLocationAddressLabel,
} from "../../../../utils/labels/shipmentLocationLabel";
import { ShipmentLocationDetails } from "../../../shipment/ShipmentSummary/ShipmentSummary";
import LinkedAssetHeader from "../../../tracking/LinkedAssetHeader/LinkedAssetHeader";
import TripLiveSummary from "../../../tracking/TripLiveSummary/TripLiveSummary";

export type TripRouteProps = {
  trip: Pick<
    Exclude<GetTripQuery["tripById"], null | undefined>,
    | "_id"
    | "status"
    | "shipmentLocations"
    | "shipments"
    | "lastTrailerLocation"
    | "lastTrailerLocationDate"
    | "tripNumber"
    | "driver"
    | "trailer"
    | "tractor"
  > & {
    polyline: Trip["polyline"];
  };
  children?: ReactNode;
  showAssetLocation?: boolean;
  useStandardMap?: boolean;
};

const TripRoute = ({ trip, children, showAssetLocation, useStandardMap }: TripRouteProps) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const center = useMemo(
    () => toLatLng(trip.shipmentLocations[0].location),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { markers: tripMarkers, polylines: tripPolylines } =
    useTripMarkersAndPath(trip);

  const assetMarkers =
    showAssetLocation && trip.lastTrailerLocation
      ? [
          createMarker(
            toLatLng(trip.lastTrailerLocation),
            {
              icon: {
                url: truckPin,
                anchor: new window.google.maps.Point(17, 17),
              },
            },
            undefined,
            {
              content: (
                <>
                  <LinkedAssetHeader
                    linkedAsset={{
                      trailer: trip.trailer,
                      tractor: trip.tractor,
                      driver: trip.driver,
                      lastKnownLocationDate: trip.lastTrailerLocationDate,
                    }}
                  />
                  <TripLiveSummary trip={trip} hideTripNumber={true} />
                </>
              ),
              extensionContent: buildAssetInfoWindowContent(trip),
            }
          ),
        ]
      : [];

  const allMarkers = [...tripMarkers, ...assetMarkers];

  return (
    <div style={{ height: "100%" }}>
      <Map
        containerStyle={{ height: "100%" }}
        center={center}
        zoom={10}
        markers={mapLoaded ? allMarkers : assetMarkers}
        polylines={mapLoaded ? tripPolylines : []}
        useStandardMap={useStandardMap}
        onLoad={(map) => {
          setMapLoaded(true);
          const bounds = new google.maps.LatLngBounds();
          trip.shipmentLocations.forEach((location) => {
            bounds.extend(toLatLng(location.location));
          });
          if (map) {
            map.fitBounds(bounds);
          }
        }}
      >
        {children}
      </Map>
    </div>
  );
};

export const useTripMarkersAndPath = (trip: TripRouteProps["trip"]) => {
  const theme = useTheme();

  const allShippedGoodsById = useMemo(
    () =>
      Object.fromEntries(
        trip.shipmentLocations
          .flatMap((l) => l.shippedGoods || [])
          .map((g) => [g._id, g])
      ),
    [trip.shipmentLocations]
  );

  const groupedLocations = useMemo(() => {
    return groupBy(
      trip.shipmentLocations.filter(
        (sl) => sl.location.latitude && sl.location.longitude
      ),
      (l) => `${l.location.latitude}-${l.location.longitude}`
    );
  }, [trip.shipmentLocations]);

  const markers: MarkerData[] = useMemo(() => {
    return map(groupedLocations, (shipmentLocationGroup) => {
      const shipmentLocation = shipmentLocationGroup[0];
      return createMarker(
        toLatLng(shipmentLocation.location),
        {
          icon: {
            url:
              shipmentLocation.locationType === ShipmentLocationType.Pickup ||
              shipmentLocation.locationType === ShipmentLocationType.TrailerPickup
                ? pickupPin
                : dropoffPin,
            anchor: new window.google.maps.Point(13.5, 13.5),
          },
        },
        undefined,
        {
          content: (
            <>
              {shipmentLocationGroup.map((location, index) => (
                <Box
                  key={index}
                  sx={{
                    "&:not(:last-of-type)": { pb: 1 },
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 600, mr: 1 }}>
                    {trip.shipmentLocations.indexOf(location) + 1}.
                  </Typography>
                  <ShipmentLocationDetails
                    shipmentLocation={location}
                    allShippedGoodsById={allShippedGoodsById}
                  />
                </Box>
              ))}
            </>
          ),
          extensionContent: buildShipmentLocationInfoWindowContent(
            shipmentLocationGroup,
            trip.shipmentLocations
          ),
        }
      );
    });
  }, [groupedLocations, trip.shipmentLocations, allShippedGoodsById]);

  const polylines: PolylineData[] = useMemo(() => {
    if (!trip.polyline) {
      return [];
    }
    return [
      createPolylineFromString(trip.polyline, {
        strokeColor: theme.palette.primary.main,
        strokeWeight: 4,
      }),
    ];
  }, [trip.polyline, theme.palette.primary.main]);

  return { markers, polylines };
};
export default TripRoute;

function buildShipmentLocationInfoWindowContent(
  shipmentLocationGroup: TripRouteProps["trip"]["shipmentLocations"],
  allLocations: TripRouteProps["trip"]["shipmentLocations"]
): InfoWindowContent {
  return shipmentLocationGroup.flatMap((location, index) => {
    const idx = allLocations.indexOf(location) + 1;
    const name = shipmentLocationLabel(location);
    const address = shipmentLocationAddressLabel(location);
    const items: InfoWindowContent = [
      {
        type: "row",
        items: [
          { type: "text", value: `${idx}.`, bold: true },
          { type: "text", value: name, bold: true },
        ],
      },
      { type: "text", value: address },
    ];
    if (index < shipmentLocationGroup.length - 1) {
      items.push({ type: "divider" });
    }
    return items;
  });
}

function buildAssetInfoWindowContent(
  trip: TripRouteProps["trip"]
): InfoWindowContent {
  const assetLabel = `${tractorLabel(trip.tractor)} / ${trailerLabel(trip.trailer)}`;
  const driver = driverLabel(trip.driver);
  const lastSeen = trip.lastTrailerLocationDate
    ? `Last seen at ${formatDateTime(new Date(trip.lastTrailerLocationDate))}`
    : null;

  const content: InfoWindowContent = [
    { type: "text", value: assetLabel, bold: true },
  ];
  if (driver) {
    content.push({ type: "text", value: driver });
  }
  if (lastSeen) {
    content.push({ type: "chip", label: lastSeen });
  }
  if (trip.tripNumber) {
    content.push({
      type: "link",
      label: `Trip ${trip.tripNumber}`,
      href: `/trips/${trip._id}`,
    });
  }
  return content;
}
